import Head from "next/head";
import React from "react";

const PrivacyPolicy = () => {
    return (
        <>
            <Head>
                <title>Lokmanya Hospital Privacy Policy | Data Protection Info</title>
                <meta
                    name="description"
                    content="Learn how Lokmanya Hospital collects, uses & safeguards your data across online & offline services. Your privacy, rights & security are our top priority."
                />
            </Head>
            <div className="side-space section-space">
                <h1>Privacy Policy</h1>
                <p>
                    Lokmanya Hospital is committed to protecting the privacy of all individuals who interact with us, whether online or offline. This Privacy Policy describes how we collect, use, share, and safeguard your personal and health information through our hospital services, website, mobile applications, appointment booking systems, and telehealth platforms. It also explains your rights regarding your information and how we comply with applicable data protection laws. By using our services (in-person or digital), you consent to the practices described in this Policy.
                </p>

                <h3>Information We Collect</h3>
                <p>We may collect various types of personal and health information from you, including:</p>
                <ul>
                    <li><strong>Personal Identification Details: </strong> Name, age/date of birth, gender, address, phone number, email, and government-issued ID details.</li>
                    <li><strong>Health and Medical Information: </strong> Medical history, symptoms, diagnoses, treatment records, test results, prescriptions, and any other health-related data you provide or that is generated in the course of your care.</li>
                    <li><strong>Appointment and Insurance Information: </strong> Appointment dates, doctor/department details, insurance provider and policy details, and payment information.</li>
                    <li><strong>Online Usage Data: </strong> Technical data such as your IP address, device type, browser type, operating system, pages or features accessed, date and time of access, and cookie data.</li>
                    <li><strong>Any Information You Choose to Provide: </strong> Feedback, inquiries, or additional details shared via forms, emails, surveys, or consultations.</li>
                    <li><strong>Telemedicine Session Data: </strong> Audio/video, chat, or documents exchanged during remote consultations (with patient consent).</li>
                    <li><strong>Children’s Data: </strong> Information of minors (&lt;18 years) collected only with parental/guardian consent.</li>
                    <li><strong>Other Voluntary Data: </strong> Feedback, complaints, surveys, or user-generated content shared with us.</li>
                </ul>

                <h3>How We Collect Information</h3>
                <ul>
                    <li><strong>Directly from You: </strong> Through registration, consultations, consent forms, or during hospital visits.</li>
                    <li><strong>Through Our Digital Platforms: </strong> Website, apps, patient portals, or telehealth platforms.</li>
                    <li><strong>Call Center and Support Services: </strong> For appointment scheduling and inquiries.</li>
                    <li><strong>Third-Party Sources (with consent): </strong> Other healthcare providers, diagnostics centers, or insurance companies.</li>
                    <li><strong>Social Media and Advertising: </strong> Interactions via social platforms or response to online campaigns.</li>
                </ul>

                <h3>Use of Personal and Health Information</h3>
                <ul>
                    <li>Provide healthcare services and treatment.</li>
                    <li>Process admissions, discharges, billing, and insurance claims.</li>
                    <li>Communicate regarding appointments, follow-ups, reports, and support requests.</li>
                    <li>Improve digital platforms and user experience.</li>
                    <li>Ensure safety, verify identity, and comply with legal requirements.</li>
                    <li>Conduct audits, training, and quality assurance.</li>
                    <li>Send health-related communications and marketing (with consent).</li>
                    <li>Use anonymized data for research and statistical analysis.</li>
                </ul>

                <h3>Sharing and Disclosure of Information</h3>
                <p>We do not disclose your information except:</p>
                <ul>
                    <li><strong>Within Lokmanya Hospital:</strong> Among authorized staff for treatment and operations.</li>
                    <li><strong>With Affiliated Providers:</strong> With consent or as necessary for care.</li>
                    <li><strong>Service Partners:</strong> Technology vendors, payment processors, or telehealth providers under confidentiality agreements.</li>
                    <li><strong>Insurance Companies:</strong> For claims and approvals with your consent.</li>
                    <li><strong>Legal Requirements:</strong> When required by law or in emergencies.</li>
                    <li><strong>With Your Authorization:</strong> For specific requests or transfers.</li>
                    <li><strong>Business Transfers:</strong> During mergers, acquisitions, or restructuring.</li>
                    <li><strong>Anonymized Data:</strong> For planning and research purposes.</li>
                </ul>

                <h3>Cookies and Online Tracking</h3>
                <p>We use cookies and tracking technologies to:</p>
                <ul>
                    <li>Remember preferences and improve user experience.</li>
                    <li>Analyze website usage via analytics tools.</li>
                    <li>Provide secure access to patient portals and services.</li>
                </ul>
                <p>Users may manage cookies via browser settings. Disabling cookies may affect website functionality.</p>

                <h3>Your Rights and Choices</h3>
                <p>You have the right to:</p>
                <ul>
                    <li>Access and review your data.</li>
                    <li>Request corrections or updates.</li>
                    <li>Withdraw consent for certain data uses.</li>
                    <li>Opt-out of marketing communications.</li>
                    <li>Disable cookies or delete your online account.</li>
                    <li>File grievances regarding data handling.</li>
                </ul>
                <p>We may verify your identity before processing such requests and will respond within applicable timelines.</p>

                <h3>Data Retention</h3>
                <ul>
                    <li>We retain records as long as required for service delivery or legal obligations.</li>
                    <li>Personal data may be anonymized for continued use in analytics.</li>
                    <li>Unneeded data is securely deleted or destroyed.</li>
                </ul>

                <h3>Data Security Measures</h3>
                <ul>
                    <li>Data is stored on secure servers with encryption.</li>
                    <li>Access is restricted to authorized personnel.</li>
                    <li>Staff are trained in data confidentiality.</li>
                    <li>Physical and IT security protocols are in place.</li>
                    <li>Breach response plans are maintained.</li>
                </ul>

                <h3>Compliance with Laws and Standards</h3>
                <p>We comply with:</p>
                <ul>
                    <li>Indian Information Technology Act and privacy rules.</li>
                    <li>Medical confidentiality obligations under Indian healthcare regulations.</li>
                    <li>International standards like HIPAA, where applicable.</li>
                    <li>Upcoming data protection legislation.</li>
                </ul>

                <h3>Changes to this Privacy Policy</h3>
                <p>We may update this Policy as needed. Significant changes will be communicated through our website or directly to users where appropriate.</p>

                <h3>Contact Us</h3>
                <p className="mb-2">For privacy-related concerns, please contact:</p>
                <p className="mb-1 fw-bold">Grievance Officer – Lokmanya Hospital</p>
                <p className="mb-1"> Email: <a className="privacy-link" href="mailto:privacy@lokmanyahospitals.com">privacy@lokmanyahospitals.com</a></p>
                <p className="mb-1">Phone: <a className="privacy-link" href="tel:+919307076767">+91-9307076767</a></p>
                <div className="d-flex gap-1">
                    <p className="mb-1">Address:</p>
                    <p className="">
                        Lokmanya Hospitals Pvt. Ltd. <br />
                        3rd Floor, 759/51,<br />
                        Deccan Pride, F.C. Road,<br />
                        Deccan Gymkhana,<br />
                        Pune - 411004, Maharashtra, India.
                    </p>
                </div>

                <p>We are committed to protecting your privacy while delivering the highest standard of care.</p>
            </div >
        </>
    );
};

export default PrivacyPolicy;
